/**
 * Helvetic Padel Series: season / registration datetimes are edited and interpreted as
 * Europe/Zurich wall time in admin, regardless of the admin’s browser timezone.
 * (Matches server logic in server/utils/swissRegistrationTime.js for DST.)
 */

const SWISS_TZ = 'Europe/Zurich';

const pad2 = (n) => String(n).padStart(2, '0');

function swissParts(date) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: SWISS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const o = {};
  for (const { type, value } of fmt.formatToParts(date)) {
    if (type !== 'literal') o[type] = value;
  }
  return {
    y: o.year,
    m: o.month,
    d: o.day,
    H: o.hour,
    M: o.minute,
    S: o.second
  };
}

function swissSortableClock(ms) {
  const p = swissParts(new Date(ms));
  return `${p.y}-${p.m}-${p.d}T${p.H}:${p.M}:${p.S}`;
}

function swissYmdHmsToUtcMs(y, mo, d, hh, mm, ss) {
  const target = `${y}-${pad2(mo)}-${pad2(d)}T${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
  let lo = Date.UTC(y, mo - 1, d, hh - 12, mm, ss);
  let hi = Date.UTC(y, mo - 1, d, hh + 12, mm, ss);
  for (let i = 0; i < 64 && lo <= hi; i++) {
    const mid = Math.floor((lo + hi) / 2);
    const cur = swissSortableClock(mid);
    if (cur < target) lo = mid + 1;
    else if (cur > target) hi = mid - 1;
    else return mid;
  }
  return Math.max(lo, hi);
}

/** DB / API instant → value for <input type="datetime-local"> (components = Swiss clock). */
export function instantToSwissDatetimeLocalValue(isoOrDate) {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  const p = swissParts(d);
  return `${p.y}-${p.m}-${p.d}T${p.H}:${p.M}`;
}

/**
 * Admin form value YYYY-MM-DDTHH:mm interpreted as Swiss local → UTC ISO for API.
 */
export function swissDatetimeLocalToUtcIso(datetimeLocal) {
  if (!datetimeLocal || !String(datetimeLocal).trim()) return null;
  const s = String(datetimeLocal).trim();
  const [datePart, timePart] = s.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  const timeBits = timePart.split(':');
  const hh = parseInt(timeBits[0], 10);
  const mm = parseInt(timeBits[1] || '0', 10);
  const ss = parseInt(timeBits[2] || '0', 10);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  const ms = swissYmdHmsToUtcMs(y, m, d, hh, mm, ss);
  return new Date(ms).toISOString();
}

/** DD/MM/YYYY in Swiss calendar for an instant. */
export function formatDateDDMMYYYYSwiss(isoOrDate) {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    timeZone: SWISS_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/** Short date + time string in Swiss local time. */
export function formatDateTimeSwiss(isoOrDate) {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    timeZone: SWISS_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export { SWISS_TZ };
